import PasswordValidator from 'password-validator';
import _ from 'lodash';
import { alarmsByStatusLegendLabels } from 'src/component/AlarmsList';
import moment from 'moment';
import { isLangBand } from './langLookUp';
import { isTextValueAOption, isTextValueASwitch, isLangATemperature } from "src/utils/langLookUp";
import { isNumber } from 'class-validator';
import JSZip from 'jszip';
import sentryService from 'src/service/sentry';
import { getTranslation, hasTranslation, translateSensorSetting } from '../i18n/translations';

export function isPasswordSecure (password) {
  const passwordSchema = new PasswordValidator();
  passwordSchema
    .is().min(8)
    .is().max(100)
    .has().lowercase()
    .has().uppercase()
    .has().digits()
    .has().symbols();

  try {
    return passwordSchema.validate(password);
  } catch (err) {
    return false;
  }
}

export function isEmailAddress (email) {
  return (/^(([^<>()[]\\.,;:\s@"]+(\.[^<>()[]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(email));
}

export function userAvatarUrl (user) {
  return user && user.getIn(['profile', 'avatarUrl']);
}

export function ucwords (str) {
  return (str + '')
    .replace(/^(.)|\s+(.)/g, function ($1) {
      return $1.toUpperCase();
    });
}

export function formatCircuitTag (circuitTag, controllerTag) {
  if (!controllerTag) {
    return `NA / ${circuitTag}`;
  }

  return `${controllerTag} / ${circuitTag}`;
}

export function formatName (firstName, lastName) {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName && !lastName) {
    return firstName;
  } else if (!firstName && lastName) {
    return lastName;
  }

  return 'N/A';
}
export function formatBytes  (bytes, decimals = 2)  {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i))?.toFixed(dm))} ${sizes[i]}`;
}

export function getFileExtension (fullpath: string) {
  return fullpath.split('.').pop();
}

export function formatDate (date) {
  return moment(date).format('YYYY-MM-DD HH:mm');
}

/**
 * Translates a validation error message, handling ERROR_CODE:label and ERROR_CODE:label:limit formats
 * This function can be used for error strings stored in the database or API responses
 *
 * @param {string} errorMessage The error message to translate (can be ERROR_CODE:label format or plain error code)
 * @param {function} getLabel optional; returns the device's own label for a setting
 * @return {string} Translated error message
 */
export function translateValidationError (errorMessage: string, getLabel?: (setting: string) => string): string {
  if (!errorMessage) {
    return errorMessage;
  }

  // Only treat it as a code when the code has a translation, so a plain message that happens to contain colons is left alone
  const parts = errorMessage.split(':');
  const codeKey = `deviceValidationErrors.${parts[0]}`;
  if ((parts.length === 2 || parts.length === 3) && hasTranslation(codeKey)) {
    const [, label, limit] = parts;
    const settingLabel = hasTranslation(label) ? translateSensorSetting(label) : (getLabel?.(label) || translateSensorSetting(label));
    return getTranslation(codeKey, { variables: { settingLabel, limit: limit ?? '' } });
  }

  // Handle old format: "Setting {label} Not Applicable to device"
  // This is for backward compatibility with errors stored before the format change
  // Match patterns like: "Setting X Not Applicable to device" (case insensitive, flexible spacing)
  const oldFormatMatch = errorMessage.match(/^Setting\s+(.+?)\s+Not\s+Applicable\s+to\s+device\s*$/i);
  if (oldFormatMatch && oldFormatMatch[1]) {
    const label = oldFormatMatch[1].trim();
    const translationKey = 'deviceValidationErrors.RECIPE_SETTING_NOT_APPLICABLE';
    const translatedLabel = translateSensorSetting(label) || getTranslation(label) || label;
    const errorTemplate = getTranslation(translationKey);
    if (errorTemplate && errorTemplate !== translationKey) {
      return errorTemplate.replace('{{settingLabel}}', translatedLabel);
    }
  }

  // Handle old format: "No known value to modify"
  if (errorMessage === 'No known value to modify') {
    return getTranslation('deviceValidationErrors.RECIPE_NO_KNOWN_VALUE_TO_MODIFY') || errorMessage;
  }

  // Try to get translation for this error code (backward compatibility)
  const translationKey = `deviceValidationErrors.${errorMessage}`;

  // if the translation key is not found, return the original message
  if (!hasTranslation(translationKey)) {
    return errorMessage;
  }

  const translatedMessage = getTranslation(translationKey);

  // Always use the translation result (either real translation or formatted label)
  return translatedMessage || errorMessage;
}

/**
 * Takes a standard API Error reponse object and builds a friendly
 * error message that can be displayed in the UI
 *
 * @param {object} err API Reponse Error Object
 * @param {object} msg Error summary message
 * @return {string} User friendly displayable Error Message
 */
export function createUserFriendlyErrorMessage (err: any, msg: string) {
  let errDescription: string | null = null;

  if (err.getIn && err.getIn(['response', 'status']) !== 500) {
    errDescription = err.getIn(['response', 'data', 'message']);
  } else if (_.get(err, 'response.status') !== 500) {
    errDescription = _.get(err, 'response.data.message');
  }

  if (errDescription) {
    return translateValidationError(errDescription);
  }

  return msg;
}

export function stringToJsonObject (inputString, value) {
  const parts = inputString.split('.');
  const result = {};

  let current = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!current[part]) {
      current[part] = {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;

  return result;
}

export function celsiusToFahrenheitConvertor (celsius) {
  // Pass null/undefined through unchanged. Coercing undefined into a number yields NaN, which
  // then defeats `!== undefined` render guards downstream (e.g. the gauge trip setpoint).
  if (celsius === null || celsius === undefined) {
    return celsius;
  }
  const fahrenheit: any = (celsius * 9/5) + 32;
  return parseFloat(fahrenheit?.toFixed(1));
}

export function fahrenheitToCelsiusConvertor (fahrenheit) {
  if (fahrenheit === null || fahrenheit === undefined) {
    return fahrenheit;
  }
  const celsius: any = (fahrenheit - 32) * 5/9;
  return parseFloat(celsius?.toFixed(3));
}

/**
 * Parse a free-text / numeric field into a finite number, or null when the
 * value is blank, missing, or non-numeric. Uses lenient `parseFloat` semantics:
 * a trailing non-numeric suffix is tolerated (e.g. '12abc' -> 12), matching how
 * forgiving free-text inputs behave. Use this (not a bare `parseFloat`) wherever
 * an empty/invalid entry must yield null rather than NaN.
 */
export function toFiniteNumber (value?: string | number | null): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function convertBand (value, tempUnit = 'F') {
  let val = Number(value);
  if (tempUnit === 'F' && val !== null) {
    // database has values in 'C' unit, so convert it here without adding/subtracting 32 number and then round it for precision
    val = val * 9 / 5;
    val = Math.round(val * 2) / 2;
    return val;
  }
  return parseFloat(val?.toFixed(1));
}

export function convertBandToCelsius (value) {
  let val = Number(value);
  if (val !== null) {
    val = val * 9/5;
    val = Math.round(val * 2) / 2;
    return val;
  }
}

// True inverse of `convertBand`: a band/delta entered in the display unit back
// to the canonical Celsius-delta (ratio 5/9, no +32 offset — a difference has
// no offset). NB `convertBandToCelsius` above is mis-named and multiplies by
// 9/5, so it is NOT this inverse; use this for display -> stored conversions.
export function convertBandFromDisplayToCelsius (value, tempUnit = 'F') {
  const val = Number(value);
  if (tempUnit === 'F') {
    return Math.round((val * 5 / 9) * 2) / 2;
  }
  return parseFloat(val?.toFixed(1));
}

export function getDisplayValueForRecipeSetting (setting, value, temperatureUnit) {
  const recipeCondition = recipeConditionOptions.find(option => option.value == setting);
  const recipeOptions = recipeCondition?.options;
  if (recipeOptions) {
    const label = recipeOptions.find(option => option.value == value)?.label;
    if (label !== undefined && label !== null) {
      return label;
    }
  }
  return temperatureUnit === 'F' && recipeCondition.isTemp ? celsiusToFahrenheitConvertor(value).toString() : value;
}

export function getDisplayLabelForRecipeSetting (setting: string) {
  const recipeConditionLabel = recipeConditionOptions.find(option => option.value == setting)?.label;
  if (recipeConditionLabel) {
    return recipeConditionLabel;
  }
  return setting;
}

export const deadLegsOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }];

export const forcedOnOptions = [
  { label: 'Yes', value: '1' },
  { label: 'No', value: '0' }];

export const forcedOffOptions = [
  { label: 'Apply', value: '1' }];

const nonfunctioningOptions = [
  { label: 'Non-Functioning', value: 'true' },
  { label: 'Functional', value: 'false' }];

export const alarmOptions = alarmsByStatusLegendLabels?.reduce((acc, value, key) => {
  acc.push({ value: key, label: value.label });
  return acc;
}, []);

export const conditionOptions = [
  { value: 'commodity', label: 'Commodity', type: 'input', conditionType: 'eht_circuit', compareOptions: ['=', '!=']  },
  { value: 'location', label: 'Location', type: 'input', conditionType: 'eht_circuit', compareOptions: ['=', '!=']  },
  { value: 'line_size', label: 'Line Size', type: 'input', conditionType: 'eht_circuit', validation: 'number' },
  { value: 'dead_legs', label: 'Dead Legs', type: 'select', options: deadLegsOptions, conditionType: 'eht_circuit', compareOptions: ['='] },
  { value: 'non_functioning', label: 'Non Functioning', type: 'select', options: nonfunctioningOptions, conditionType: 'eht_alarm', compareOptions: ['='] },
  { value: 'alarm', label: 'Alarm', type: 'select', options: alarmOptions, conditionType: 'eht_alarm', compareOptions: ['='] },
  { value: 'actual_temperature', label: 'Process Temp', type: 'input', conditionType: 'eht_alarm', validation: 'number', isTemp: true },
  { value: 'low', label: 'Ambient Temp', type: 'input', conditionType: 'eht_alarm', validation: 'number', isTemp: true },
  { value: 'priority', label: 'Priority', type: 'input', conditionType: 'eht_circuit',  compareOptions: ['=', '!='] }
];

export const tierOptions = [
  { value: 'alpha', label: 'Alpha' },
  { value: 'beta', label: 'Beta' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'smarttrace-monitoring', label: 'SmartTrace Monitoring' },
  { value: 'smarttrace-monitoring-plus', label: 'SmartTrace Monitoring Plus' },
  { value: 'smarttrace-management', label: 'SmartTrace Management' },
  { value: 'smarttrace-enterprise', label: 'SmartTrace Enterprise' },
  { value: 'atcom-monitoring', label: 'Atcom Plus Monitoring' },
  { value: 'atcom-monitoring-plus', label: 'Atcom Plus Monitoring Plus' },
  { value: 'atcom-management', label: 'Atcom Plus Management' },
  { value: 'atcom-enterprise', label: 'Atcom Plus Enterprise' }
];

const tempSensorsRecipeOptions = [];
for (let i=1; i<=8; i++) {
  tempSensorsRecipeOptions.push(
    { value: `temperatureSensors.${i}.lowTemperatureAlarmSetpoint`, label: `TS ${i} Low Temperature Alarm Setpoint`, type: 'input', conditionType: 'eht_alarm', validation: 'number', modificationOption: ['=', '+', '-'] },
    { value: `temperatureSensors.${i}.highTemperatureAlarmSetpoint`, label: `TS ${i} High Temperature Alarm Setpoint`, type: 'input', conditionType: 'eht_alarm', validation: 'number', modificationOption: ['=', '+', '-'] }
  );
}

export const recipeConditionOptions: any = [
  { value: 'controlBand', label: 'Control Band', type: 'input', conditionType: 'eht_circuit', validation: 'number', modificationOption: ['=', '+', '-'] },
  { value: 'controlTemperatureSetpoint', label: 'Control Temperature', type: 'input', conditionType: 'eht_circuit', validation: 'number', modificationOption: ['=', '+', '-'], isTemp: true },
  { value: 'lowControlTemperatureAlarmSetpoint', label: 'Low Control Temperature Alarm Setpoint', type: 'input', conditionType: 'eht_alarm', validation: 'number', modificationOption: ['=', '+', '-'], isTemp: true },
  { value: 'highControlTemperatureAlarmSetpoint', label: 'High Control Temperature Alarm Setpoint', type: 'input', conditionType: 'eht_alarm', validation: 'number', modificationOption: ['=', '+', '-'], isTemp: true },
  { value: 'lowLoadCurrentAlarmSetpoint', label: 'Low Load Current Alarm Setpoint', type: 'input', conditionType: 'eht_circuit', validation: 'number', modificationOption: ['=', '+', '-']  },
  { value: 'highLoadCurrentAlarmSetpoint', label: 'High Load Current Alarm Setpoint', type: 'input', conditionType: 'eht_circuit', validation: 'number', modificationOption: ['=', '+', '-'] },
  { value: 'groundFaultTripCurrentSetpoint', label: 'Ground Fault Trip Current Setpoint', type: 'input', conditionType: 'eht_circuit', validation: 'number', modificationOption: ['=', '+', '-'] },
  { value: 'highGroundCurrentAlarmSetpoint', label: 'High Ground Current Alarm Setpoint', type: 'input', conditionType: 'eht_alarm', validation: 'number', modificationOption: ['=', '+', '-'] },
  { value: 'forcedOn', label: 'Forced On', defaultVal: '0', type: 'select', options: forcedOnOptions, conditionType: 'eht_circuit', modificationOption: ['='] },
  { value: 'forcedOff', label: 'Forced Off', defaultVal: 1, type: 'select', options: forcedOffOptions, conditionType: 'eht_circuit', modificationOption: ['='] },
  ...tempSensorsRecipeOptions
];

export const defaultAllowedCircuitTypes: any = {
  ATCOMPlusController: ['ATCOMPlus'],
  MasterTraceMSController: ['MasterTraceMS'],
  MSCPController: ['MSCP'],
  NVentAC2000P: ['NVentCM2000P'],
  NVentNGC20Controller: ['NVentNGC20'],
  NVentNGC30Controller: ['NVentNGC30'],
  NVentNGC40Controller: ['NVentNGC40', 'NVentNGC40HTC3', 'NVentNGC40IO'],
  SmartTraceController: ['SmartTrace'],
  SmartTraceMPController: ['SmartTraceMP'],
  ThermonGenesisController: ['ThermonGenesis'],
  ThermonTC101Controller: ['ThermonTC101'],
  ThermonTC201Controller: ['ThermonTC201'],
  ThermonTC202Controller: ['ThermonTC202'],
  ThermonTC1818Controller: ['ThermonTC1818'],
  ThermonTCM2Controller: ['ThermonTCM2'],
  ThermonTCM18Controller: ['ThermonTCM18'],
  ChromaloxITLSC1D2EXTController: ['ChromaloxITLSC1D2EXT'],
  Elexant4020iController: ['Elexant4020i'],
  Elexant4010iController: ['Elexant4010i']

};

export const comparingText = {
  '=': 'is equal to',
  '<': 'is less than',
  '<=': 'is less than or equal to',
  '>': 'is greater than',
  '>=': 'is greater than or equal to',
  '!=': 'is not equal to'
};

export function addSelectedFiltersToQuery (selectedFilters, query) {
  if (!selectedFilters) {
    return query;
  }

  if (selectedFilters.get('unit')) {
    query.unit = selectedFilters.get('unit');
  }

  if (selectedFilters.get('system')) {
    query.system = selectedFilters.get('system');
  }

  if (selectedFilters.get('commodity')) {
    query.commodity = selectedFilters.get('commodity');
  }

  if (selectedFilters.get('location')) {
    query.location = selectedFilters.get('location');
  }

  if (selectedFilters.get('priority')) {
    query.priority = selectedFilters.get('priority');
  }

  if (selectedFilters.get('controller')) {
    query.controllerId = selectedFilters.get('controller');
  }

  if (selectedFilters.get('deadleg')) {
    query.deadleg = selectedFilters.get('deadleg') === 'Yes';
  }
  if (selectedFilters.get('deviceGroup')) {
    query.deviceGroup = selectedFilters.get('deviceGroup');
  }
  if (selectedFilters.get('attribute')) {
    query.attribute = selectedFilters.get('attribute');
  }

  return query;
}

export function addOptionalFilters (optionalFilters, query) {
  if (!optionalFilters) {
    return query;
  }

  if (optionalFilters.get('controllerId')) {
    query.controllerId = optionalFilters.get('controllerId');
  }
  if (optionalFilters.get('panelId')) {
    query.panelId = optionalFilters.get('panelId');
  }
  if (optionalFilters.get('circuitId')) {
    query.circuitId = optionalFilters.get('circuitId');
  }
  if (optionalFilters.get('deviceId')) {
    query.deviceId = optionalFilters.get('deviceId');
  }
  if (optionalFilters.get('batchId')) {
    query.batchId = optionalFilters.get('batchId');
  }
  if (optionalFilters.get('userSearchInput')) {
    query.filter = optionalFilters.get('userSearchInput');
  }
  if (optionalFilters.get('sort')) {
    query.sort = optionalFilters.get('sort');
  }
  if (optionalFilters.get('noAlarmHasNotification')) {
    query.noAlarmHasNotification = optionalFilters.get('noAlarmHasNotification');
  }
  if (optionalFilters.get('withAlarmSummary')) {
    query.withAlarmSummary = optionalFilters.get('withAlarmSummary');
  }
  if (optionalFilters.get('deviceFilters')) {
    query.deviceFilters = optionalFilters.get('deviceFilters');
  }
  if (optionalFilters.get('filter')) {
    query.filter = optionalFilters.get('filter');
  }

  return query;
}

export const isWhitelabel = () => {
  return window.location.href.includes('atcom.plus');
};

export const formatNumberByCommas = (number) => {
  if (typeof number !== 'number') {
    return number;
  }
  const roundedNumber = roundToNearestDecimal(number, 1);
  const formatted = roundedNumber.toString();
  const numberParts = formatted.split('.');
  const numberWithCommas = numberParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  numberParts[0] = numberWithCommas;
  return numberParts.join('.');
};

export const hexToRgb = (hex, alpha = 1) => {
  // Remove the '#' if present
  hex = hex.replace('#', '');

  // Parse the hex string into RGB components
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgb(${r}, ${g}, ${b}, ${alpha})`;
};

export const formatDiscrepancyValue = (setting, valueFromSetting, deviceConfigFields, temperatureUnit) => {
  const isTextAOption = isTextValueAOption(setting);
  const isSwitch = isTextValueASwitch(setting);
  const isTemp = isLangATemperature(setting);
  const isPartialTemp = isLangBand(setting);

  let value = temperatureUnit === 'F' && isTemp && valueFromSetting !== 'null' ? celsiusToFahrenheitConvertor(valueFromSetting) : valueFromSetting;
  // some setting are dropdowns and we need to find their correct label
  if (isTextAOption) {
    const options = deviceConfigFields.getIn(['settings', setting?.split('.').join(''), 'options']);
    value = options?.find(x => x.get('value').toString() === value.toString())?.get('label') || 'Invalid value';
  } else if (isPartialTemp) {
    value = convertBand(value, temperatureUnit );
  } else if (isSwitch) {
    value = value ? 'On' : 'Off';
  } else if (isNumber(value)) {
    value = formatNumberByCommas(value);
  }
  return value;
};
// Friendly name for a setting: the device's own label if it has one, otherwise the translated key.
// Nested settings (e.g. temperatureSensors.1.type) are matched on the config field's path.
export const getSettingLabel = (setting, deviceConfigFields, t = getTranslation) => {
  const settings = deviceConfigFields?.get('settings');
  const label = settings?.getIn([setting, 'label'])
    || settings?.find(field => field?.get('path') === setting)?.get('label');
  return label || t(setting);
};
const getExceptionCode = (data: Array<number> | Buffer) => {
  let exception = -1;
  if (data && data.length && data.length >= 3) {
    const functionCode = data[1];
    if (functionCode > 128) {
      exception = data[2];
    }
  }
  return exception;
};
export const parseModbusResponse =(response) => {
  if (!response || !response.length || response.length < 3) {
    throw new Error("Invalid modbus response!");
  }
  const exception = getExceptionCode(response);
  if (exception >= 0) {
    throw new Error(`Generated modbus exception ${exception}`);
  }

  const unitId = response[0];
  const functionCode = response[1];
  const byteCount = response[2];

  if (response.length !== 3 + byteCount) {
    throw new Error("Byte count does not match response length");
  }

  const registers = [];
  for (let i = 3; i < 3 + byteCount; i += 2) {
    const value = (response[i] << 8) | response[i + 1];
    registers.push(value);
  }

  return {
    unitId,
    functionCode,
    registers
  };
};

export const HasAbility = (can: string | Array<string>, abilities: Array<string>) => {
  if (typeof can === 'string' && !abilities.includes(can)) {
    return false;
  } else {
    for (let i = 0; i < can.length; i++) {
      if (!abilities.includes(can[i])) {
        return false;
      }
    }
  }
  return true;
};

export const unsignedToSigned16bit = (n) => {
  return n & 0x8000 ? n - 0x10000 : n;
};

export const isInteger = (str) => {
  return /^-?\d+$/.test(str);
};

export async function fetchAndConcatLogZip (data): Promise<string> {
  const zip = await JSZip.loadAsync(data);

  // Extract text from all files, ordered alphabetically
  const files = Object.values(zip.files)
    .filter((f) => !f.dir)
    .sort((a, b) => a.name.localeCompare(b.name));

  const contents = await Promise.all(
    files.map((file) => file.async('text'))
  );

  return contents.join('\n');
}

let cachedApiAddr: string | null = null;
let cachedManifest: any | null = null;

async function getManifest (): Promise<any> {
  if (cachedManifest) {
    return cachedManifest;
  }

  try {
    // manifest.json doubles as the on-prem runtime config: the installer injects
    // ON_PREM_FOX_API_ADDR into it post-build at this fixed path, so it can never be
    // content-hashed. The request itself must therefore bypass the HTTP cache, or a
    // stale copy points the dashboard at the wrong API host. no-store also covers the
    // on-prem nginx, which sets no Cache-Control at all.
    const response = await fetch('/manifest.json', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`manifest.json fetch failed with status ${response.status}`);
    }
    cachedManifest = await response.json();
    return cachedManifest;
  } catch (error) {
    // Deliberately not cached: caching {} here would pin the app to the fallback API
    // address for the whole session after a single transient failure at startup.
    sentryService.captureException(error).then(() => {});
    return {};
  }
}

export async function getApiAddr (): Promise<string> {
  if (cachedApiAddr) {
    return cachedApiAddr;
  }

  const ensureProtocol = (addr: string): string => {
    if (!addr.startsWith('http://') && !addr.startsWith('https://')) {
      return `http://${addr}`;
    }
    return addr;
  };

  try {
    const manifest = await getManifest();

    if (manifest.ON_PREM_FOX_API_ADDR) {
      cachedApiAddr = ensureProtocol(manifest.ON_PREM_FOX_API_ADDR);
    } else {
      const envAddr = import.meta.env.REACT_APP_FOX_API_ADDR || 'http://localhost:9644';
      cachedApiAddr = ensureProtocol(envAddr);
    }

    return cachedApiAddr;
  } catch (error) {
    const envAddr = import.meta.env.REACT_APP_FOX_API_ADDR || 'http://localhost:9644';
    cachedApiAddr = ensureProtocol(envAddr);
    return cachedApiAddr;
  }
}

export async function getAutobahnAddr (): Promise<string> {
  const apiAddr = await getApiAddr();
  let autobahnAddr = apiAddr.replace('http://', 'ws://').replace('https://', 'wss://');
  autobahnAddr = autobahnAddr + '/v1/autobahn';
  return autobahnAddr;
}

export function isControllerLikeCircuit (deviceFactory: string) {
  return deviceFactory === 'NVent920' || deviceFactory === 'NVent910'
    || deviceFactory === 'NVentNGC20' || deviceFactory === 'Elexant4020i'
    || deviceFactory === 'Elexant3500i' || deviceFactory === 'Elexant4010i'
    || deviceFactory === 'Elexant5010i' || deviceFactory === 'Raychem7300i'
    || deviceFactory === 'RaptorPLC';
}

// Factories that behave like the NVent 900 series i.e they share the NVent9xxBase Modbus map
// A subaddress is therefore mandatory when adding one.
export function isLike900Series (deviceFactory: string) {
  return deviceFactory === 'NVent920' || deviceFactory === 'NVent910' || deviceFactory === 'Raychem7300i';
}

export function isNGC40Family (deviceFactory: string) {
  return deviceFactory === 'NVentNGC40' || deviceFactory === 'NVentNGC40HTC3' || deviceFactory === 'NVentNGC40IO';
}

export const rolesNameToKey = (roleName: string): string => {
  const roles = {
    'Administrator': 'admin',
    'Regular User': 'regular_user',
    'Super Admin': 'super_admin',
    'Maintenance User': 'maintenance_user',
    'EHT Specialist': 'eht_specialist',
    'Operations': 'operations',
    'SmartTrace Admin': 'smarttrace_admin'
  };
  return roles[roleName] || roleName;
};

export const getValueForSetting = (value: any, setting: string, isFahrenheit: boolean = false) => {
  if (isFahrenheit && isLangBand(setting)) {
    return convertBand(value);
  }
  if (isFahrenheit) {
    return celsiusToFahrenheitConvertor(value);
  }
  return value;
};

export const roundToNearestDecimal = (number, decimalPlaces = 1) => {
  return Math.round(number * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

export const useEmail = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_USE_EMAIL ?? import.meta.env.REACT_APP_USE_EMAIL;
  return value !== 'false';
};

export const useSMS = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_SMS_ENABLED ?? import.meta.env.REACT_APP_SMS_ENABLED;
  return value !== 'false';
};

export const usePDFServices = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_PDF_SERVICES_ENABLED ?? import.meta.env.REACT_APP_PDF_SERVICES_ENABLED;
  return value !== 'false';
};

export const useSSO = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_SSO_ENABLED ?? import.meta.env.REACT_APP_SSO_ENABLED;
  return value !== 'false';
};

export const useAlarmReports = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_ALARM_REPORTS_ENABLED ?? import.meta.env.REACT_APP_ALARM_REPORTS_ENABLED;
  return value !== 'false';
};

export const onPremEnabled = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_ON_PREM_ENABLED ?? import.meta.env.REACT_APP_ON_PREM_ENABLED;
  return value === 'true';
};

export const useAdvancedSearch = async () => {
  const manifest = await getManifest();
  const value = manifest.REACT_APP_USE_ADVANCED_SEARCH ?? import.meta.env.REACT_APP_USE_ADVANCED_SEARCH;
  return value === 'true';
};

/**
 * Sets the moment.js locale based on the provided language code
 * @param {string} language - Language code (e.g., 'fr', 'en')
 */
export const setMomentLocale = (language: string) => {
  moment.locale(language === 'fr' ? 'fr' : 'en');
};

/**
 * Converts Immutable.js structures or arrays to a plain JavaScript array.
 * Handles various Immutable types (List, Map, etc.) and falls back gracefully.
 * @param {any} options - The options value (can be array, Immutable List, or other iterable)
 * @returns {Array|null} - Plain JavaScript array or null if conversion fails
 */
export const convertToArray = (options: any): Array<any> | null => {
  if (!options) return null;

  // Already a plain array
  if (Array.isArray(options)) {
    return options;
  }

  // Immutable.js structures with toJS method
  if (typeof options.toJS === 'function') {
    return options.toJS();
  }

  // Immutable.js List with toArray method
  if (typeof options.toArray === 'function') {
    return options.toArray();
  }

  // Try Array.from for iterables (with fallback to toJS)
  if (typeof options.map === 'function') {
    try {
      return Array.from(options);
    } catch (e) {
      // Fallback to toJS if available
      if (options.toJS) {
        return options.toJS();
      }
    }
  }

  return null;
};
